import React, { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Star, Quote, Home, Clock } from "lucide-react";
import { useLang } from "@/i18n";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const CountUp = ({ raw }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(raw);

  useEffect(() => {
    if (!inView) return;
    const m = raw.match(/^([^\d]*)([\d.,]+)(.*)$/);
    if (!m || raw.includes("/")) {
      setDisplay(raw);
      return;
    }
    const [, prefix, numStr, suffix] = m;
    const hasSep = numStr.includes(".") || numStr.includes(",");
    const sepChar = numStr.includes(".") ? "." : ",";
    const target = parseInt(numStr.replace(/[.,]/g, ""), 10);
    const fmt = (n) => (hasSep && n >= 1000 ? n.toLocaleString("nl-NL").replace(/[.,]/g, sepChar) : String(n));

    const duration = 1200;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(`${prefix}${fmt(Math.round(target * eased))}${suffix}`);
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, raw]);

  return <span ref={ref}>{display}</span>;
};

const STAT_ICONS = [Home, Clock, Star];

const SocialProof = () => {
  const { t } = useLang();
  const s = t.social;

  return (
    <section className="px-5 py-24 md:px-8">
      <div className="mx-auto max-w-7xl">
        <Reveal className="mx-auto max-w-2xl text-center">
          <div className="mb-3 flex items-center justify-center gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
            ))}
          </div>
          <h2 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{s.title}</h2>
          <p className="mt-4 text-lg text-slate-600">{s.subtitle}</p>
        </Reveal>

        <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-3">
          {s.stats.map((st, i) => {
            const Icon = STAT_ICONS[i] || Star;
            return (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`stat-${i}`} className="flex flex-col items-center rounded-3xl border border-slate-100 bg-blue-50/50 p-8 text-center shadow-sm">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-blue-700">
                    <Icon className="h-6 w-6" />
                  </span>
                  <p className="mt-4 font-heading text-4xl font-bold text-blue-700 sm:text-5xl">
                    <CountUp raw={st.v} />
                  </p>
                  <p className="mt-2 text-sm font-medium text-slate-700">{st.l}</p>
                  {st.note && <p className="mt-1 text-xs text-slate-400">{st.note}</p>}
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {s.testimonials.map((tm, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div data-testid={`testimonial-${i}`} className="flex h-full flex-col rounded-3xl border border-slate-100 bg-white p-7 shadow-sm">
                <Quote className="h-8 w-8 text-blue-200" />
                <p className="mt-4 flex-1 text-base leading-relaxed text-slate-700">"{tm.text}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-600 font-heading text-sm font-bold text-white">
                    {tm.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{tm.name}</p>
                    <p className="text-sm text-slate-500">{tm.city}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
